import React, { useState } from 'react';
import { EventFormData, TicketData, VenueData, TicketGroupData } from './types';
import NewTicketModal from './Components/NewTicketModal';
import TicketGroupModal from './Components/TicketGroupModal';
import LearnMoreModal from './Components/LearnMoreModal';
import TicketList from './Components/TicketList';
import TicketGroupList from './Components/TicketGroupList';
import ConfirmationDialog from '../../../components/common/ConfirmationDialog';
import { Ticket, Plus } from 'lucide-react';

interface EventTicketsTabProps {
  tickets: TicketData[];
  onInputChange: (field: keyof EventFormData, value: any) => void;
  activeTab: number;
  hasMultipleVenues?: boolean;
  venues?: VenueData[];
  hasMultipleTicketGroups?: boolean;
  ticketGroups?: TicketGroupData[];
  isTicketFree?: boolean;
  absorbFees?: boolean;
  editingTicket?: TicketData;
}

const EventTicketsTab: React.FC<EventTicketsTabProps> = ({
  tickets,
  onInputChange,
  activeTab,
  hasMultipleVenues = false,
  venues = [],
  hasMultipleTicketGroups = false,
  ticketGroups = [],
  isTicketFree = false,
  absorbFees = false
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isTicketGroupModalOpen, setIsTicketGroupModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [ticketKind, setTicketType] = useState<'single' | 'group'>('single');
  const [editingTicketGroup, setEditingTicketGroup] = useState<TicketGroupData | undefined>(undefined);
  const [editingTicket, setEditingTicket] = useState<TicketData | undefined>(undefined);
  const [learnMoreModal, setLearnMoreModal] = useState(false);
  const [learnMoreModalField, setLearnMoreModalField] = useState('');
  const [type, setType] = useState<boolean>(false);
  
  const [confirmationDialog, setConfirmationDialog] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
    type: 'warning' as 'warning' | 'danger' | 'info' | 'success'
  });

  const toggleModal = () => {
    if (isModalOpen) {
      setEditingTicket(undefined);
      if (!editingTicket) {
        setTicketType('single');
      }
    }
    setIsModalOpen(!isModalOpen);
    setType(false);
  };

  React.useEffect(() => {
    if (editingTicket) {
      setType(editingTicket.isGroupTicket ? true : false);
    }
    return () => setType(false);
  }, [editingTicket]);

  const showConfirmationDialog = (title: string, message: string, onConfirm: () => void, type: 'warning' | 'danger' | 'info' | 'success' = 'warning') => {
    setConfirmationDialog({
      isOpen: true,
      title,
      message,
      onConfirm,
      type
    });
  };

  const closeConfirmationDialog = () => {
    setConfirmationDialog(prev => ({ ...prev, isOpen: false }));
  };

  const openModal = (type: 'single' | 'group') => {
    setTicketType(type);
    setIsModalOpen(true);
  };

  const handleAddTicketGroup = () => {
    setEditingTicketGroup(undefined);
    setIsTicketGroupModalOpen(true);
  };

  const handleEditTicketGroup = (group: TicketGroupData) => {
    setEditingTicketGroup(group);
    setIsTicketGroupModalOpen(true);
  };

  const handleSaveTicketGroup = (groupData: TicketGroupData) => {
    setIsLoading(true);
    try {
      let updatedGroups = [...ticketGroups];
      if (editingTicketGroup) {
        const index = updatedGroups.findIndex(g => String(g.id) === String(editingTicketGroup.id));
        if (index !== -1) {
          updatedGroups[index] = { ...groupData, id: editingTicketGroup.id };
        }
      } else {
        const newGroup: TicketGroupData = {
          ...groupData,
          id: Date.now() as any,
          groupName: groupData.name || '',
          slugId: '',
          status: 'active',
          isSlotRequired: false,
          createdAt: '',
          updatedAt: ''
        };
        updatedGroups.push(newGroup);
      }
      onInputChange('ticketGroups', updatedGroups);
      setIsTicketGroupModalOpen(false);
      setEditingTicketGroup(undefined);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteTicketGroup = (groupId: string | number) => {
    const groupToDelete = ticketGroups.find(g => String(g.id) === String(groupId));
    if (groupToDelete) {
      showConfirmationDialog(
        'Delete Ticket Group',
        `Are you sure you want to delete group "${groupToDelete.name || groupToDelete.groupName}"?`,
        () => {
          const updatedGroups = ticketGroups.filter(g => String(g.id) !== String(groupId));
          onInputChange('ticketGroups', updatedGroups);
        },
        'danger'
      );
    }
  };

  const handleAbsorbFeesToggle = (newValue: boolean) => {
    if (tickets.length > 0 && absorbFees !== newValue) {
      showConfirmationDialog(
        'Change Fee Settings',
        `Changing fee settings will clear all ${tickets.length} existing ticket(s).`,
        () => {
          onInputChange('absorbFees', newValue);
          onInputChange('tickets', []);
        },
        'warning'
      );
    } else {
      onInputChange('absorbFees', newValue);
    }
  };

  const handleMultipleTicketGroupsToggle = (hasMultiple: boolean) => {
    if (tickets.length > 0 && hasMultipleTicketGroups !== hasMultiple) {
      showConfirmationDialog(
        'Change Ticket Group Settings',
        `Changing settings will clear all ${tickets.length} existing ticket(s).`,
        () => {
          onInputChange('hasMultipleTicketGroups', hasMultiple);
          onInputChange('tickets', []);
          if (!hasMultiple) onInputChange('ticketGroups', []);
        },
        'warning'
      );
    } else {
      onInputChange('hasMultipleTicketGroups', hasMultiple);
      if (!hasMultiple) onInputChange('ticketGroups', []);
    }
  };

  const toggleLearnMoreModal = (field: string) => {
    setLearnMoreModalField(field);
    setLearnMoreModal(!learnMoreModal);
  };

  const handleTicketSubmit = async (values: any) => {
    setIsLoading(true);
    try {
      if (editingTicket) {
        const updatedTickets = tickets.map(t =>
          (t.id || t.ticketTypeId) === (editingTicket.id || editingTicket.ticketTypeId)
            ? ({
                ...t,
                name: values.ticketType || values.name || '',
                ticketType: values.ticketType || values.name || '',
                type: ticketKind,
                isGroupTicket: ticketKind === 'group',
                description: values.description ?? '',
                price: isTicketFree ? 0 : Number(values.price || 0),
                totalTickets: values.maxCount === 0 || values.maxCount === null || isNaN(Number(values.maxCount)) ? null : (Number(values.maxCount) as any),
                groupTicketCount: ticketKind === 'group' ? String(values.groupTicketCount) : undefined,
                venueId: hasMultipleVenues ? values.venueId : undefined,
                ticketGroupId: hasMultipleTicketGroups ? values.groupingId : undefined,
                groupingId: hasMultipleTicketGroups ? values.groupingId : '',
                isOfferActive: values.isOfferActive || false,
                previousAmount: values.isOfferActive ? Number(values.previousAmount) : undefined,
                ticketOrder: String(values.ticketOrder || t.ticketOrder || 1),
              } as any)
            : t
        );
        onInputChange('tickets', updatedTickets);
      } else {
        const newTicket: TicketData = {
          id: Date.now().toString(),
          ticketTypeId: Date.now().toString(),
          ticketType: values.ticketType || values.name || '',
          name: values.ticketType || values.name || '',
          type: ticketKind,
          isGroupTicket: ticketKind === 'group',
          description: values.description ?? '',
          price: isTicketFree ? 0 : Number(values.price || 0),
          totalTickets: values.maxCount === 0 || values.maxCount === null || isNaN(Number(values.maxCount)) ? null : (Number(values.maxCount) as any),
          maxCount: String(values.maxCount || 0),
          ticketStatus: 'active',
          currentCount: '0',
          createdAt: '',
          updatedAt: '',
          eventSlug: '',
          groupingId: hasMultipleTicketGroups ? values.groupingId : '',
          maxPerCustomer: -1,
          ticketOrder: String(values.ticketOrder || tickets.length + 1),
          earlyBirdDiscount: false,
          discountAmount: 0,
          discountType: 'percentage',
          discountEndDate: '',
          discountEndTime: '',
          isActive: true,
          groupTicketCount: ticketKind === 'group' ? String(values.groupTicketCount) : undefined,
          venueId: hasMultipleVenues ? values.venueId : undefined,
          ticketGroupId: hasMultipleTicketGroups ? values.groupingId : undefined,
          isOfferActive: values.isOfferActive || false,
          previousAmount: values.isOfferActive ? Number(values.previousAmount) : undefined,
        } as any;
        onInputChange('tickets', [...tickets, newTicket]);
      }
      setEditingTicket(undefined);
      setIsModalOpen(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTicketToggle = (ticketId: string, isActive: boolean) => {
    const updatedTickets = tickets.map(t =>
      (t.id || t.ticketTypeId) === ticketId ? { ...t, isActive } : t
    );
    onInputChange('tickets', updatedTickets);
  };

  const onEditTicket = (ticket: TicketData) => {
    setEditingTicket(ticket);
    setTicketType((ticket.type || 'single') as 'single' | 'group');
    setIsModalOpen(true);
  };

  const onDeleteTicket = (ticketId: string) => {
    const ticketToDelete = tickets.find(t => (t.id || t.ticketTypeId) === ticketId);
    if (ticketToDelete) {
      showConfirmationDialog(
        'Delete Ticket',
        `Are you sure you want to delete ticket "${ticketToDelete.name || ticketToDelete.ticketType}"?`,
        () => {
          const updatedTickets = tickets.filter(t => (t.id || t.ticketTypeId) !== ticketId);
          onInputChange('tickets', updatedTickets);
        },
        'danger'
      );
    }
  };

  const handleReorderTickets = (reorderedTickets: TicketData[]) => {
    onInputChange('tickets', reorderedTickets);
  };

  return (
    <div className={`step-tab-panel ${activeTab === 2 ? 'active' : ''}`} style={{ display: activeTab === 2 ? "flex" : "none", flexDirection: "column", gap: 24 }}>
      
      {/* Card 1: Ticketing Fee Rules */}
      {!isTicketFree && (
        <div className="glass-panel" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div className="v3-section-head">
            <Ticket size={14} color="#EE3F7C" /> Ticketing Fee Rules
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>Organizer Absorbs Fees</div>
              <p style={{ fontSize: 11, color: "rgba(255,255,255,0.45)", margin: "4px 0 0 0" }}>
                Deduct ticketing fees from revenue or add them to checkout total.
                <span className="text-primary ms-1" style={{ cursor: 'pointer' }} onClick={() => toggleLearnMoreModal('absorbFees')}> Learn more</span>
              </p>
            </div>
            <button
              type="button"
              className={`v3-toggle ${absorbFees ? "on" : "off"}`}
              onClick={() => handleAbsorbFeesToggle(!absorbFees)}
            >
              <span className="v3-toggle-knob" />
            </button>
          </div>
        </div>
      )}

      {/* Card 2: Ticket Group Structure */}
      <div className="glass-panel" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <div className="v3-section-head">
          <Ticket size={14} color="#EE3F7C" /> Ticket Group Structure
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>Multiple Ticket Groups</div>
            <p style={{ fontSize: 11, color: "rgba(255,255,255,0.45)", margin: "4px 0 0 0" }}>
              Organize your event tickets into multiple categories or groups.
              <span className="text-primary ms-1" style={{ cursor: 'pointer' }} onClick={() => toggleLearnMoreModal('multipleTicketGroups')}> Learn more</span>
            </p>
          </div>
          <button
            type="button"
            className={`v3-toggle ${hasMultipleTicketGroups ? "on" : "off"}`}
            onClick={() => handleMultipleTicketGroupsToggle(!hasMultipleTicketGroups)}
          >
            <span className="v3-toggle-knob" />
          </button>
        </div>

        {hasMultipleTicketGroups && (
          <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <h6 style={{ margin: 0, color: "#fff" }}>Ticket Groups ({ticketGroups.length})</h6>
              <button type="button" className="v3-btn-secondary" style={{ fontSize: 12 }} onClick={handleAddTicketGroup}>+ Add Ticket Group</button>
            </div>
            <TicketGroupList ticketGroups={ticketGroups} onEditTicketGroup={handleEditTicketGroup} onDeleteTicketGroup={handleDeleteTicketGroup} />
          </div>
        )}
      </div>

      {/* Card 3: Ticket Architect */}
      <div className="glass-panel" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <div className="v3-section-head">
          <Ticket size={14} color="#EE3F7C" /> Ticket Architect ({tickets.length})
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>Configure Ticket Tiers</div>
            <p style={{ fontSize: 11, color: "rgba(255,255,255,0.45)", margin: "4px 0 0 0" }}>Add single admission passes or group bundles.</p>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button type="button" className="v3-btn-secondary" style={{ fontSize: 12, padding: "8px 16px" }} onClick={() => openModal("single")}>
              <Plus size={14} /> Single Ticket
            </button>
            <button type="button" className="v3-btn-secondary" style={{ fontSize: 12, padding: "8px 16px" }} onClick={() => openModal("group")}>
              <Plus size={14} /> Group Bundle
            </button>
          </div>
        </div>

        {tickets.length === 0 ? (
          <div style={{ padding: "32px 16px", textAlign: "center", borderRadius: 14, background: "rgba(255,255,255,0.02)", border: "1px dashed rgba(255,255,255,0.08)" }}>
            <Ticket size={28} color="#EE3F7C" style={{ margin: "0 auto 8px auto", display: "block" }} />
            <div style={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>No ticket tiers created</div>
            <p style={{ fontSize: 11, color: "rgba(255,255,255,0.45)", marginTop: 4, marginBottom: 12 }}>Click above to configure pricing structures.</p>
          </div>
        ) : (
          <TicketList
            tickets={tickets}
            hasMultipleVenues={hasMultipleVenues}
            venues={venues}
            hasMultipleTicketGroups={hasMultipleTicketGroups}
            ticketGroups={ticketGroups}
            onTicketToggle={handleTicketToggle}
            onEditTicket={onEditTicket}
            onDeleteTicket={onDeleteTicket}
            onReorderTickets={handleReorderTickets}
          />
        )}
      </div>

      <NewTicketModal
        isOpen={isModalOpen}
        toggle={toggleModal}
        onSubmit={handleTicketSubmit}
        isLoading={isLoading}
        ticketKind={ticketKind}
        hasMultipleVenues={hasMultipleVenues}
        venues={venues}
        hasMultipleTicketGroups={hasMultipleTicketGroups}
        ticketGroups={ticketGroups}
        isTicketFree={isTicketFree}
        editingTicket={editingTicket}
        type={type}
      />
      <TicketGroupModal
        isOpen={isTicketGroupModalOpen}
        toggle={() => setIsTicketGroupModalOpen(false)}
        onSubmit={handleSaveTicketGroup}
        isLoading={isLoading}
        ticketGroup={editingTicketGroup}
      />
      <ConfirmationDialog
        isOpen={confirmationDialog.isOpen}
        toggle={closeConfirmationDialog}
        onConfirm={confirmationDialog.onConfirm}
        title={confirmationDialog.title}
        message={confirmationDialog.message}
        type={confirmationDialog.type}
        confirmText="Yes, Continue"
        cancelText="Cancel"
        icon="fa-solid fa-exclamation-triangle"
      />
      <LearnMoreModal
        isOpen={learnMoreModal}
        toggle={() => setLearnMoreModal(false)}
        field={learnMoreModalField}
        fieldValue={absorbFees}
      />
    </div>
  );
};

export default EventTicketsTab;